import {
	ContractorListItem,
	ContractorTypeLabels,
	ContractorTypeValues,
	DesignerTypeLabels,
	PAGES,
} from "@dune/types";
import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	PaginatedDataTable,
} from "@dune/ui";
import { ChevronDown, Search } from "lucide-react";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePaginationStore } from "../../../stores/pagination.store";
import { useGetContractorsList } from "../../../api/queries/contractors.queries";
import { useGetInsurers } from "../../../api/queries/insurer.queries";
import { contractorsColumns } from "./contractorsColumns";
import {
	isValidContractorType,
	isValidDesignerType,
	parseAsContractorType,
	parseAsDesignerType,
	parseAsInsurerType,
} from "./contractorsParser";

const ContractorsListPage = () => {
	const navigate = useNavigate();
	const [search, setSearch] = useQueryState("search", parseAsString);
	const [category, setCategory] = useQueryState(
		"category",
		parseAsContractorType,
	);
	const [type, setType] = useQueryState("type", parseAsDesignerType);
	const [insurer, setInsurer] = useQueryState("insurer", parseAsInsurerType);
	const storedLimit = usePaginationStore((s) => s.limit);
	const setStoredLimit = usePaginationStore((s) => s.setLimit);
	const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
	const [limitParam, setLimitParam] = useQueryState(
		"limit",
		parseAsInteger.withDefault(storedLimit),
	);
	const limit = limitParam ?? storedLimit;

	const { data: contractorsResponse, isLoading } = useGetContractorsList({
		search: search || undefined,
		category: category || undefined,
		type: type || undefined,
		insurer: insurer || undefined,
		page,
		limit,
	});

	const contractors = contractorsResponse?.data || [];
	const paginationMeta = contractorsResponse?.meta || {
		page,
		limit,
		total: 0,
		totalPages: 1,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const { data: insurers = [] } = useGetInsurers();

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const handleLimitChange = (newLimit: number) => {
		setLimitParam(newLimit);
		setStoredLimit(newLimit);
		setPage(1);
	};

	const handleRowClick = useCallback(
		(contractor: ContractorListItem) => {
			navigate(PAGES.CONTRACTORS.show.withParams(contractor.id.toString()));
		},
		[navigate],
	);

	const contractorCounts = useMemo(() => {
		const counts: Record<ContractorTypeValues, number> = {
			DESIGNER: 0,
			BUILDER: 0,
			MATERIAL: 0,
		};

		for (const contractor of contractors) {
			if (isValidContractorType(contractor.type)) {
				counts[contractor.type] = (counts[contractor.type] || 0) + 1;
			}
		}

		return {
			total: contractors.length,
			DESIGNER: counts.DESIGNER,
			BUILDER: counts.BUILDER,
			MATERIAL: counts.MATERIAL,
		};
	}, [contractors]);

	return (
		<div className="flex flex-col h-full">
			<div className="flex flex-col gap-6 p-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900">
							Intervenants
						</h1>
					</div>
				</div>

				<div className="grid grid-cols-8 items-center gap-4">
					<div className="relative min-w-64 w-full col-span-2 bg-white">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
						<Input
							placeholder="Recherche rapide"
							value={search || ""}
							onChange={(e) => {
								setSearch(e.target.value || null);
								setPage(1);
							}}
							className="pl-10"
						/>
					</div>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="w-full h-full justify-between"
							>
								{category && category !== "all"
									? ContractorTypeLabels[category]
									: "Catégorie"}
								<ChevronDown className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-full">
							<DropdownMenuItem
								onClick={() => {
									setCategory("all");
									setPage(1);
								}}
								className={!category || category === "all" ? "bg-accent" : ""}
							>
								Toutes
							</DropdownMenuItem>
							{Object.entries(ContractorTypeLabels)
								.filter(([key]) => key !== "MATERIAL")
								.map(([key, label]) => (
									<DropdownMenuItem
										key={key}
										onClick={() => {
											if (isValidContractorType(key)) {
												setCategory(key);
												setPage(1);
											}
										}}
										className={category === key ? "bg-accent" : ""}
									>
										{label}{" "}
										{isValidContractorType(key) && contractorCounts[key]
											? `(${contractorCounts[key]})`
											: ""}
									</DropdownMenuItem>
								))}
						</DropdownMenuContent>
					</DropdownMenu>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="w-full h-full justify-between"
							>
								{type && type !== "all" ? DesignerTypeLabels[type] : "Type"}
								<ChevronDown className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-full">
							<DropdownMenuItem
								onClick={() => {
									setType("all");
									setPage(1);
								}}
								className={!type || type === "all" ? "bg-accent" : ""}
							>
								Tous
							</DropdownMenuItem>
							{Object.entries(DesignerTypeLabels).map(([key, label]) => (
								<DropdownMenuItem
									key={key}
									onClick={() => {
										if (isValidDesignerType(key)) {
											setType(key);
											setPage(1);
										}
									}}
									className={type === key ? "bg-accent" : ""}
								>
									{label}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className="w-full h-full justify-between"
							>
								{insurer && insurer !== "all" ? insurer : "Assureur"}
								<ChevronDown className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-full">
							<DropdownMenuItem
								onClick={() => {
									setInsurer("all");
									setPage(1);
								}}
								className={!insurer || insurer === "all" ? "bg-accent" : ""}
							>
								Tous
							</DropdownMenuItem>
							{insurers.map((insurerOption) => (
								<DropdownMenuItem
									key={insurerOption}
									onClick={() => {
										setInsurer(insurerOption);
										setPage(1);
									}}
									className={insurer === insurerOption ? "bg-accent" : ""}
								>
									{insurerOption}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className="bg-white rounded-lg border">
					<PaginatedDataTable
						variant="default"
						columns={contractorsColumns}
						data={contractors}
						meta={paginationMeta}
						onPageChange={handlePageChange}
						onLimitChange={handleLimitChange}
						isLoading={isLoading}
						noResultsText="Aucun intervenant trouvé"
						onRowClick={handleRowClick}
					/>
				</div>
			</div>
		</div>
	);
};

export default ContractorsListPage;
